<template>
  <div class="qrcode-stream-wrapper min-h-[220px]">
    <video
      ref="video"
      :class="{ 'qrcode-stream-camera--hidden': !shouldScan }"
      class="qrcode-stream-camera"
      autoplay
      muted
      playsinline
    ></video>
    <canvas
      ref="pauseFrame"
      v-show="!shouldScan"
      class="qrcode-stream-camera"
    ></canvas>
    <canvas ref="trackingLayer" class="qrcode-stream-overlay"></canvas>
    <div class="qrcode-stream-overlay h-full" @click="init()">
      <div
        v-if="!cameraInstance"
        class="w-full h-full flex flex-col cursor-pointer items-center justify-center border border-dashed border-gray-300 bg-white p-6 hover:border-success"
      >
        <div class="flex items-center justify-between flex-col pb-8 relative">
          <DeviceComputerCameraIcon class="text-success mb-4" size="48" />
          <span class="hover:text-success text-center">{{
            dataLocale.data.title
          }}</span>
          <button
            class="h-10 mt-4 px-5 font-semibold bg-success text-sm sm:text-base text-white focus-visible:shadow-button-focus-success whitespace-nowrap transition inline-flex items-center justify-center rounded-full"
          >
            <SmallIcon class="text-white mr-1" />
            {{ dataLocale.data.button }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <ScannerIssueDeviceCamera ref="issue_camera" />
</template>

<script>
import { keepScanning } from '~/config/misc/scanner.js';
import Camera from '~/config/misc/camera.js';
import { DeviceComputerCameraIcon } from 'vue-tabler-icons';
import { useScanner } from '~~/stores/scanner';
import SmallIcon from '~/assets/svg/homepage/small.svg?component';
import { useNotification } from '@kyvg/vue3-notification';
import DataLocale from '~~/config/interface/DataLocale';
export default {
  components: { DeviceComputerCameraIcon, SmallIcon },
  setup() {
    const scanner = useScanner();
    const notify = useNotification();

    return {
      scanner,
      notify,
    };
  },
  props: {
    camera: {
      type: String,
      default: 'auto',
      validator(camera) {
        return ['auto', 'rear', 'front', 'off'].includes(camera);
      },
    },
    torch: {
      type: Boolean,
      default: false,
    },

    track: {
      type: Function,
    },
  },

  data() {
    const dataLocale = new DataLocale([
      {
        locale: 'en',
        title: 'Enable the camera and aim at the QR code picture',
        button: 'Open Camera',
        permission: 'Kindly grant us access to your camera, please!',
      },
      {
        locale: 'vi',
        title: 'Bật camera và nhắm vào hình ảnh mã QR',
        button: 'Mở Camera',
        permission: 'Vui lòng cấp quyền truy cập máy ảnh của bạn!',
      },
      {
        locale: 'fr',
        title: 'Activez la caméra et visez la photo du code QR',
        button: 'Ouvrir la caméra',
        permission:
          "Veuillez nous accorder l'accès à votre appareil photo, s'il vous plaît!",
      },
      {
        locale: 'it',
        title: 'Abilita la fotocamera e puntala sulla foto del codice QR',
        button: 'Apri fotocamera',
        permission: "Gentilmente concedeteci l'accesso alla vostra telecamera",
      },
      {
        locale: 'pt',
        title: 'Ative a câmera e aponte para a imagem do código QR',
        button: 'Abrir câmera',
        permission: 'Por favor, conceda-nos acesso à sua câmera!',
      },
      {
        locale: 'es',
        title: 'Active la cámara y apunta a la imagen del código QR',
        button: 'Abrir cámara',
        permission: 'Por favor, otorgue acceso a su cámara!',
      },
      {
        locale: 'de',
        title: 'Aktivieren Sie die Kamera und zielen Sie auf das QR-Code-Bild',
        button: 'Kamera öffnen',
        permission: 'Bitte erlauben Sie uns den Zugriff auf Ihre Kamera.',
      },
      {
        locale: 'nl',
        title: 'Activeer de camera en richt op de QR-codefoto',
        button: 'Camera openen',
        permission: 'Graag toegang verlenen tot uw camera, alstublieft!',
      },
      {
        locale: 'ja',
        title: 'カメラを有効にしてQRコード画像を狙ってください',
        button: 'カメラを開く',
        permission: 'カメラへのアクセスを許可してください！',
      },
      {
        locale: 'zh',
        title: '启用相机并对准QR码图片',
        button: '打开相机',
        permission: '请允许我们访问您的摄像头！',
      },
    ]);
    return {
      cameraInstance: null,
      destroyed: false,
      dataLocale,
    };
  },

  computed: {
    shouldStream() {
      return this.destroyed === false && this.camera !== 'off';
    },

    shouldScan() {
      return this.shouldStream === true && this.cameraInstance !== null;
    },

    /**
     * Minimum delay in milliseconds between frames to be scanned. Don't scan
     * so often when visual tracking is disabled to improve performance.
     */
    scanInterval() {
      if (this.track === undefined) {
        return 500;
      } else {
        return 40; // ~ 25fps
      }
    },
  },

  watch: {
    $route(v) {
      this.beforeResetCamera();
    },
    shouldStream(shouldStream) {
      if (!shouldStream) {
        const canvas = this.$refs.pauseFrame;
        const ctx = canvas.getContext('2d');
        const video = this.$refs.video;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
      }
    },

    shouldScan(shouldScan) {
      if (shouldScan) {
        this.clearCanvas(this.$refs.pauseFrame);
        this.clearCanvas(this.$refs.trackingLayer);
        this.startScanning();
      }
    },

    torch() {
      this.init();
    },

    camera() {
      this.init();
    },
  },
  beforeDestroy() {
    this.beforeResetCamera();
    this.destroyed = true;
  },
  methods: {
    init() {
      const promise = (async () => {
        this.beforeResetCamera();
        if (this.camera === 'off') {
          this.cameraInstance = null;
          return {
            capabilities: {},
          };
        } else {
          this.cameraInstance = await Camera(this.$refs.video, {
            camera: this.camera,
            torch: this.torch,
          });
          const capabilities = this.cameraInstance.getCapabilities();
          // if the component is destroyed before `cameraInstance` resolves a
          // `beforeDestroy` hook has no chance to clear the remaining camera
          // stream.
          if (this.destroyed) {
            this.cameraInstance.stop();
          }
          this.scrollToCamera();
          return {
            capabilities,
          };
        }
      })().catch((err) => {
        if ((err + '').includes('Permission denied')) {
          this.notify.notify({
            text: this.dataLocale.data.permission,
            type: 'error',
          });
        } else {
          this.scanner.camera_failded = true;
        }
      });
    },
    scrollToCamera() {
      try {
        var top = this.$refs.video.getBoundingClientRect().top;
        window.scrollBy(0, top - 150);
      } catch (error) {}
    },
    startScanning() {
      const detectHandler = async (result) => {
        // scanner.startScanning()
        const { content } = await result;
        this.scanner.response = [
          {
            content,
            file: '',
            name: '',
          },
        ];
        this.scanner.openModal();
        this.scanner.scanning = false;
        this.beforeResetCamera();
        const canvas = this.$refs.trackingLayer;
        this.clearCanvas(canvas);
      };

      keepScanning(this.$refs.video, {
        detectHandler,
        locateHandler: this.onLocate,
        minDelay: this.scanInterval,
      });
    },

    beforeResetCamera() {
      if (this.cameraInstance !== null) {
        this.cameraInstance.stop();
        this.cameraInstance = null;
      }
    },

    onLocate(detectedCodes) {
      const canvas = this.$refs.trackingLayer;
      const video = this.$refs.video;

      if (canvas !== undefined) {
        if (
          detectedCodes.length > 0 &&
          this.track !== undefined &&
          video !== undefined
        ) {
          // The visually occupied area of the video element.
          // Because the component is responsive and fills the available space,
          // this can be more or less than the actual resolution of the camera.
          const displayWidth = video.offsetWidth;
          const displayHeight = video.offsetHeight;

          // The actual resolution of the camera.
          // These values are fixed no matter the screen size.
          const resolutionWidth = video.videoWidth;
          const resolutionHeight = video.videoHeight;

          // Dimensions of the video element as if there would be no
          //   object-fit: cover;
          // Thus, the ratio is the same as the cameras resolution but it's
          // scaled down to the size of the visually occupied area.
          const largerRatio = Math.max(
            displayWidth / resolutionWidth,
            displayHeight / resolutionHeight,
          );
          const uncutWidth = resolutionWidth * largerRatio;
          const uncutHeight = resolutionHeight * largerRatio;

          const xScalar = uncutWidth / resolutionWidth;
          const yScalar = uncutHeight / resolutionHeight;
          const xOffset = (displayWidth - uncutWidth) / 2;
          const yOffset = (displayHeight - uncutHeight) / 2;

          const scale = ({ x, y }) => {
            return {
              x: Math.floor(x * xScalar),
              y: Math.floor(y * yScalar),
            };
          };

          const translate = ({ x, y }) => {
            return {
              x: Math.floor(x + xOffset),
              y: Math.floor(y + yOffset),
            };
          };

          const adjustedCodes = detectedCodes.map((detectedCode) => {
            const { boundingBox, cornerPoints } = detectedCode;

            const { x, y } = translate(
              scale({
                x: boundingBox.x,
                y: boundingBox.y,
              }),
            );
            const { x: width, y: height } = scale({
              x: boundingBox.width,
              y: boundingBox.height,
            });

            return {
              ...detectedCode,
              cornerPoints: cornerPoints.map((point) =>
                translate(scale(point)),
              ),
              boundingBox: DOMRectReadOnly.fromRect({ x, y, width, height }),
            };
          });

          canvas.width = video.offsetWidth;
          canvas.height = video.offsetHeight;

          const ctx = canvas.getContext('2d');

          this.track(adjustedCodes, ctx);
        } else {
          this.clearCanvas(canvas);
        }
      }
    },

    repaintTrackingLayer(video, canvas, location) {
      const ctx = canvas.getContext('2d');

      window.requestAnimationFrame(() => {
        canvas.width = displayWidth;
        canvas.height = displayHeight;

        this.trackRepaintFunction(coordinatesAdjusted, ctx);
      });
    },

    clearCanvas(canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    },
  },
};
</script>
<style lang="css" scoped>
.qrcode-stream-wrapper {
  width: 100%;
  position: relative;
  z-index: 0;
}

.qrcode-stream-overlay {
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.qrcode-stream-camera {
  margin: auto;
  display: block;
  object-fit: cover;
}

.qrcode-stream-camera--hidden {
  visibility: hidden;
  position: absolute;
}
</style>
